/**
 * API Configuration
 */

import { ensureFileToken, fileTokenLejaroban, getFileToken } from "@/utils/file-token";

export const VERSION_NUMBER = "1.22.0";
export const APP_DEBUG = import.meta.env.VITE_APP_DEBUG === "true";

const envApiUrl = import.meta.env.VITE_APP_API_BASE_URL;

export const API_CONFIG = {
	BASE_URL: envApiUrl || (APP_DEBUG ? "/api" : "/api"),
} as const;

const envReverbUrl = import.meta.env.VITE_REVERB_URL;
export const REVERB_URL = envReverbUrl || (APP_DEBUG ? "reverb.nexusnet.hu" : "reverb.lineelectronic.com");

const envStorageUrl = import.meta.env.VITE_STORAGE_URL;
export const STORAGE_URL = envStorageUrl || (APP_DEBUG ? "/files" : "/files");

/**
 * A fájl-kiszolgálás URL-előtagja.
 *
 * A tokent a SZERVER adja (belépéskor, illetve a /api/files/token
 * végpontról), a felhasználóhoz kötött és lejár.
 *
 * Korábban a felület maga számolta: `md5(dátum + VITE_SECRET_KEY)`. Az a
 * „titok” viszont a publikus JS-kötegbe fordult bele, élesen pedig be sem
 * volt állítva — így a tokent bárki kiszámolhatta a dátumból, és azzal
 * bármelyik dokumentum letölthető volt.
 */
export const getStorageUrl = (): string => {
	return `${STORAGE_URL}/${getFileToken()}`;
};

/**
 * Fájl megnyitása új lapon, GARANTÁLTAN érvényes tokennel.
 *
 * ⚠️ Azért nem elég egy `await ensureFileToken()` a `window.open()` előtt: a
 * böngésző felugró-blokkolója csak a felhasználói kattintás pillanatában
 * engedi a `window.open()`-t. Egy `await` után az már „nem kattintásból jött",
 * és a megnyitás CSENDBEN elmarad — vagyis a lejárt tokent csak egy másik
 * hibára cserélnénk.
 *
 * Ezért: ha a token még jó (a szokásos eset), semmi nem változik. Ha lejárt
 * vagy hamarosan lejár, előbb SZINKRONBAN megnyitjuk az üres lapot — így
 * megmarad a kattintás jogosultsága —, és csak utána irányítjuk a friss
 * tokenes címre.
 */
export const openStorageFile = (utvonal: string, fajlNev?: string): void => {
	if (/^https?:\/\//.test(utvonal)) {
		window.open(utvonal, "_blank", "noopener,noreferrer");
		return;
	}

	const tiszta = utvonal.replace(/^\/+/, "");

	/*
	 * Ha tudjuk a fájl eredeti nevét, `<a download>`-dal mentünk — különben a
	 * böngésző a tárolt ULID-nevet adná a letöltésnek (`01K7ABC.pdf`), ami a
	 * felhasználónak használhatatlan. A /files ugyanazon a kiszolgálón van,
	 * mint a felület, ezért a `download` attribútumot a böngésző elfogadja.
	 */
	const megnyit = (token: string) => {
		const url = `${STORAGE_URL}/${token}/${tiszta}`;

		if (!fajlNev) {
			window.open(url, "_blank", "noopener,noreferrer");
			return;
		}

		const link = document.createElement("a");
		link.href = url;
		link.download = fajlNev;
		link.rel = "noopener noreferrer";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	if (!fileTokenLejaroban()) {
		megnyit(getFileToken());
		return;
	}

	/*
	 * Lejárt token. Letöltésnél (van fájlnév) egyszerűen megvárjuk a frisset:
	 * a `<a download>` kattintást a felugró-blokkoló nem érinti.
	 *
	 * ⚠️ Új lap nyitásánál viszont ELŐBB meg kell nyitni az üres lapot, mert a
	 * `window.open()`-t a böngésző csak a kattintás pillanatában engedi — egy
	 * `await` után a megnyitás CSENDBEN elmaradna.
	 */
	if (fajlNev) {
		void ensureFileToken().then(megnyit);
		return;
	}

	const ablak = window.open("about:blank", "_blank", "noopener,noreferrer");

	void ensureFileToken().then((friss) => {
		const vegleges = `${STORAGE_URL}/${friss}/${tiszta}`;
		if (ablak && !ablak.closed) {
			ablak.location.replace(vegleges);
		} else {
			window.open(vegleges, "_blank", "noopener,noreferrer");
		}
	});
};

/**
 * Helper function to build API URLs
 */
export const buildApiUrl = (endpoint: string): string => {
	return `${API_CONFIG.BASE_URL}${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`;
};
